import { useMemo } from 'react';
import type { SettingsPco } from '@coffman-tm/shared';
import { formatPcoNumber } from '../../lib/format';
import { Dropdown } from '../design';

/**
 * Standard dropdown with all active PCOs as options. Auto-selects the
 * suggested PCO id when the caller passes one; otherwise renders the
 * "Needs assignment" placeholder. The caller controls `value` and gets
 * change notifications via `onChange` — this component is intentionally
 * dumb so the block card owns the resolved selection state.
 */
export interface PcoAssignmentDropdownProps {
  pcos: SettingsPco[];
  value: string;
  onChange: (pcoId: string) => void;
  disabled?: boolean;
}

export function PcoAssignmentDropdown({
  pcos,
  value,
  onChange,
  disabled,
}: PcoAssignmentDropdownProps) {
  // Filter to active-ish PCOs (open + active) so Joey doesn't accidentally
  // assign new work to a closed / paid bucket. We keep PCOs in pending_approval
  // / approved in the list because mid-week saves under those happen.
  const options = useMemo(
    () => [
      // A real option, not the placeholder: "" is a legitimate, storable state
      // (the block is unassigned) and has to be re-selectable.
      { value: '', label: '— Needs assignment —' },
      ...pcos
        .filter((p) => p.status !== 'void')
        .map((p) => ({
          value: p.id,
          label: `PCO ${formatPcoNumber(p.pco_number)} — ${p.description}`,
        })),
    ],
    [pcos],
  );
  return (
    <Dropdown
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-label="PCO"
      style={{ minWidth: 280 }}
    />
  );
}
